import {Box, Tabs, Text} from '@mantine/core';
import Head from 'next/head';
import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import {type FC, useEffect} from 'react';
import {FaChevronLeft} from 'react-icons/fa';
import {routes} from '../../../config/constants';
import {COLOR_PRIMARY} from '../../../config/constants/colorCodes';
import {GSSKT_HOST} from '../../../config/constants/hosts';
import {
  ENGINEER_GUILD_LOGO,
  ENGINEER_PROBLEM_OGP,
} from '../../../config/constants/img';
import isAppWebView from '../../../config/isAppWebView';
import type {EngineerProblemDetailType} from '../../../core/entities/company/schema';
import {validateCookie} from '../../../core/usecases/auth/checkOauthTokenExistence';
import {CommonLink} from '../../../shared/components/CommonLink';
import {createBreadCrumbsStructuredData} from '../../../shared/services/createBreadCrumbsStructuredData';
import {DrillRequirementCard} from './Presenter/DrillRequirementCard';
import {ShareButtonsForDrill} from './Presenter/ShareButtonsForDrill';

type EngineerDrillPageProps = {
  problem: EngineerProblemDetailType;
};

export const EngineerDrillPage: FC<EngineerDrillPageProps> = ({problem}) => {
  const cookies = parseCookies();
  const {isValid} = validateCookie(cookies);

  const router = useRouter();
  const problemId = String(router.query.id);
  const currentStep = Number(router.query.step) || 1;
  const currentUrl = `${GSSKT_HOST}${decodeURI(router.asPath)}`;
  const TITLE = `${problem.title} - Software Engineer 就活 by 外資就活ドットコム`;

  const crumbs = [
    {label: 'Home', href: '/'},
    {label: 'Software Engineer 就活', href: '/engineer'},
    {label: '開発ドリル', href: '/engineer/drill'},
    {label: problem.title, href: `/engineer/drill/${problemId}/1`},
  ];
  const description = problem.summary;

  const snsText = (() => {
    const requirement = problem?.stepRequirements?.[0]?.[0];
    const problemTags = requirement?.problemTags ?? [];
    const technologyTags = requirement?.technologyTags ?? [];
    return `【あなたもプロダクト開発を経験しよう！】
#${[...problemTags, ...technologyTags].join(' #')}
#外資就活 #開発ドリル`;
  })();

  useEffect(() => {
    if (isAppWebView() && !isValid) {
      void router.push(`/login?next_redirect_url=${routes.ENGINEER}`);
    }
  }, [isValid, router.push]);

  return (
    <>
      <Head>
        <title key="title">{TITLE}</title>
        <meta key="og:title" property="og:title" content={TITLE} />
        <meta
          key="og:site_name"
          property="og:site_name"
          content="外資就活ドットコム"
        />
        <meta key="og:url" property="og:url" content={currentUrl} />
        <meta
          key="og:image"
          property="og:image"
          content={`${GSSKT_HOST}${ENGINEER_PROBLEM_OGP}`}
        />
        <meta
          key="og:description"
          property="og:description"
          content={description}
        />
        <meta key="description" name="description" content={description} />
        <meta
          key="keywords"
          name="keywords"
          content="外資,就活,新卒,エンジニア,技術職,エンジニア就活サイト,外資IT企業,新卒エンジニア外資,外資系IT新卒採用,テクノロジー外資就職,外資系ソフトウェア開発,プログラミング求人外資,外資系エンジニアキャリア,外資系開発職就活,グローバルIT企業求人,外資系技術職,システムエンジニア,ソフトウェアエンジニア求人,フロントエンドエンジニア,バックエンドエンジニア,AIエンジニア,データサイエンティスト,セキュリティエンジニア,エンジニアコミュニティ"
        />
        <link rel="canonical" href={currentUrl} key="canonical" />
        <script type="application/ld+json">
          {JSON.stringify(createBreadCrumbsStructuredData(crumbs))}
        </script>
      </Head>

      {isAppWebView() ? null : (
        <Box className="w-[100%] pc:w-[668px] mt-64">
          <CommonLink href="../" className="font-bold block">
            <FaChevronLeft className="inline-block align-middle mr-4" />
            <span className="align-middle">一覧に戻る</span>
          </CommonLink>
        </Box>
      )}

      <Box className="w-[100%] pc:w-[668px] mx-16 mt-32 max-w-[100%] rounded-12 bg-white overflow-hidden">
        <Box className="aspect-[668/348]">
          {problem.thumbnailUrl && (
            <img
              src={problem.thumbnailUrl}
              alt={problem.title}
              className="w-full h-full"
            />
          )}
        </Box>
        <Box className="p-16">
          <Box className="mb-12">
            <Text className="font-bold text-md">{problem.title}</Text>
          </Box>
          <Box className="text-[14px]">
            <Text className="text-black-1 whitespace-pre-wrap">
              {problem.summary}
            </Text>
          </Box>
        </Box>
        <Tabs
          value={`${currentStep}`}
          styles={() => ({
            tab: {
              '&[data-active]': {
                borderBottomColor: COLOR_PRIMARY,
                color: COLOR_PRIMARY,
                opacity: 1,
              },
            },
          })}
          onChange={() => {
            location.pathname = '/';
          }}
        >
          <Tabs.List className="justify-center items-center mb-16 flex-nowrap">
            {problem.stepRequirements.map((_, index) => (
              <Tabs.Tab
                // biome-ignore lint/suspicious/noArrayIndexKey: order won't change
                key={index}
                className="flex-1 font-bold text-black opacity-60"
                style={{textOverflow: 'ellipsis'}}
                value={`${index + 1}`}
                onClick={async () => {
                  await router.push(
                    `/engineer/drill/${problemId}/${index + 1}`,
                    undefined,
                    {scroll: false},
                  );
                }}
              >
                ステップ{index + 1}
              </Tabs.Tab>
            ))}
          </Tabs.List>

          {problem.stepRequirements.map((requirements, index) => (
            <Tabs.Panel
              value={`${index + 1}`}
              // biome-ignore lint/suspicious/noArrayIndexKey: order won't change
              key={index}
            >
              <Tabs
                defaultValue={'1'}
                styles={() => ({
                  tab: {
                    '&[data-active]': {
                      borderColor: COLOR_PRIMARY,
                      color: COLOR_PRIMARY,
                      opacity: 1,
                    },
                  },
                })}
              >
                {requirements.length > 1 && (
                  <Tabs.List className="justify-center items-center mb-16 border-b-0 gap-8">
                    {requirements.map((requirement, index) => (
                      <Tabs.Tab
                        className="border-solid border-t-2 border-r-2 border-l-2 border-b-2 border-black text-black opacity-30 rounded-full font-bold text-[12px]"
                        value={`${index + 1}`}
                        key={requirement.step}
                      >
                        {requirement.title}
                      </Tabs.Tab>
                    ))}
                  </Tabs.List>
                )}
                {requirements.map((requirement, index) => (
                  <Tabs.Panel value={`${index + 1}`} key={requirement.step}>
                    <DrillRequirementCard requirement={requirement} />
                    <Box className="w-[100%] p-12">
                      <ShareButtonsForDrill
                        title={snsText}
                        currentUrl={currentUrl}
                      />
                    </Box>
                  </Tabs.Panel>
                ))}
              </Tabs>
            </Tabs.Panel>
          ))}
        </Tabs>
      </Box>
      <Box className="p-16 mt-8 w-[668px] max-w-[100%] rounded-12 bg-white">
        <img
          src={ENGINEER_GUILD_LOGO}
          alt="ソフトウェアエンジニア志望の方向けコミュニティ 『Engineer Guild』"
          className="w-7/12 h-auto mb-16 mx-auto pc:w-5/12"
        />
        <Text className="font-bold text-base mb-4">
          ソフトウェアエンジニア志望の方向けコミュニティ 『Engineer Guild』
        </Text>
        <Text className="text-line text-sm mb-6">
          わからないことがある時は、ソフトウェアエンジニア志望の方向けコミュニティ『Engineer
          Guild』で検索したりほかのユーザーに聞いてみましょう。
        </Text>
        <CommonLink
          href="https://event.gaishishukatsu.com/gsskt-enginner-guild"
          target="_blank"
          className="rounded-full bg-primary-1 text-white text-base font-bold px-16 py-8 mt-8 float-right"
        >
          相談する
        </CommonLink>
      </Box>
    </>
  );
};
